import { AssetType, type AssetTypeRepository } from '@telespot/domain';
import { ParseBaseRepository } from '../../parse-base.repository';
import { AssetTypeTopology, ParseAssetTypeMapper } from './parse-asset-type.mapper';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';

export class ParseAssetTypeRepository extends ParseBaseRepository implements AssetTypeRepository {
  private static readonly COUNT_ASSET_TYPES_PAGINATION_LIMIT = 25;

  private readonly assetTypeMapper = new ParseAssetTypeMapper(this.parse);

  /**
   * `undefined` when the asset type does not exist or is soft-deleted (the
   * `beforeFind` hook hides those, so Parse answers OBJECT_NOT_FOUND). Any
   * other failure — network, permissions — propagates: callers must not mistake
   * a broken read for a missing row.
   */
  public async getById(id: string): Promise<AssetType | undefined> {
    try {
      const assetType = await new this.parse.Query(AssetTypeTopology.TABLE).get(id, this.options);

      return this.assetTypeMapper.toDomain(assetType);
    } catch (error: unknown) {
      if (error instanceof this.parse.Error && error.code === this.parse.Error.OBJECT_NOT_FOUND) {
        return undefined;
      }
      throw error;
    }
  }

  public async getAll(searchTerm?: string, limit?: number, lastItemName?: string): Promise<AssetType[]> {
    const query = this.buildAssetTypeQuery(searchTerm).ascending(AssetTypeTopology.NAME);

    if (limit) query.limit(limit);
    if (lastItemName) query.greaterThan(AssetTypeTopology.NAME, lastItemName);

    const parseAssetTypes = await query.find(this.options);
    return parseAssetTypes.map((pat) => this.assetTypeMapper.toDomain(pat));
  }

  public async count(searchTerm?: string): Promise<number> {
    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = this.buildAssetTypeQuery(searchTerm);
      query.ascending(ObjectTopology.ID);
      query.select(); // Only fetch objectIds to minimize data transfer
      query.limit(ParseAssetTypeRepository.COUNT_ASSET_TYPES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseAssetTypeRepository.COUNT_ASSET_TYPES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  public async delete(id: string): Promise<void> {
    const parseAssetType = this.subclasses.getSubclass(AssetTypeTopology.TABLE).createWithoutData(id);
    await parseAssetType.save(
      {
        [SoftDeletableTopology.DELETED_AT]: new Date(),
      },
      this.options
    );
  }

  public async save(input: AssetType): Promise<string> {
    const { id } = await this.assetTypeMapper.fromDomain(input).save(null, this.options);
    return id;
  }

  private buildAssetTypeQuery(searchTerm?: string): Parse.Query {
    const query = new this.parse.Query(AssetTypeTopology.TABLE);

    if (searchTerm && searchTerm.trim() !== '') {
      query.matches(AssetTypeTopology.NAME, new RegExp(this.escapeRegExp(searchTerm), 'i'));
    }

    return query;
  }
}
